// AuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getUser } from 'api/users';
import { userLogin, userLogout } from 'api/login';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

/**
 * AuthProvider component
 *
 * Provides authentication context for the application, managing user state,
 * authentication processes, and job application preferences.
 *
 * State Management:
 * - user: current authenticated user information
 * - loading: loading state for authentication processes
 * - jobApply: state for tracking job application status
 * - jobPreferenceData: state for storing job preference data
 *
 * Effects:
 * - Fetches and sets user data on component mount
 * - Listens for visibility changes to refresh user data if needed
 *
 * Context Functions:
 * - login: authenticates user and updates context state
 * - logout: clears user data and local storage
 * - updateContext: updates user state with provided payload
 * - clearContext: clears user data and local storage
 *
 * @param {{children: React.ReactNode}} props - The children components requiring authentication context
 *
 * @returns {React.ReactElement} The provider component wrapping children with authentication context
 */

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [jobApply, setJobApply] = useState('');
    const [jobPreferenceData, setJobPreferenceData] = useState({});
    const [showJobStatusDialog, setShowJobStatusDialog] = useState(false);

    useEffect(() => {
        /**
         * Fetches the current authenticated user from the server and updates the user state.
         * Sets the loading state to false after the fetch attempt, regardless of success or failure.
         * If the fetch is successful, updates the user state with the retrieved data.
         * If an error occurs, sets the user state to null.
         */

        const fetchUser = async () => {
            try {
                const response = await getUser();
                setUser(response?.data);
            } catch (error) {
                setUser(null);
            }
            setLoading(false);
        };

        fetchUser();

        /**
         * Listens for visibility changes to the document and refetches the user data from the server
         * if the document becomes visible. This is useful when the user switches between tabs or
         * windows and the data may have changed while the user was away.
         */

        const handleVisibilityChange = async () => {
            if (document.visibilityState === "visible") {
                await fetchUser();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, []);

    /**
     * Authenticates a user using the provided payload.
     * @param {Object} payload - The payload containing user login credentials.
     * @returns {Promise<Object>} - A promise that resolves with the authentication response.
     */
    const login = async (payload) => {
        try {
            const response = await userLogin(payload);
            if (response?.success) setUser(response?.data);
            return { ...response };
        } catch (error) {
            console.error(error);
            return { success: false, ...error };
        }
    };

    /**
     * Logs out the user from the application.
     * Clears the user context and local storage, and sets the loading state to false.
     * @returns {Promise} - A promise that resolves with no value.
     */
    const logout = async () => {
        setLoading(true);
        try {
            await userLogout({ id: user?.id });
            setUser(null);
            localStorage.clear();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Updates the user context with the provided payload.
     *
     * This function merges the current user state with the payload,
     * allowing specific user properties to be updated. If any error
     * occurs during the update, it logs the error to the console.
     *
     * @param {Object} payload - The object containing properties to update
     * the user state with. If not provided, the current state remains unchanged.
     */

    const updateContext = (payload) => {
        try {
            setUser((prevousState) => {
                return { ...prevousState, ...(payload || {}) }
            });
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * Clears the user context and local storage.
     *
     * This function sets the user state to null and clears
     * all data stored in the local storage. It is typically
     * used during the logout process to ensure no residual
     * user data remains in the application state.
     */

    const clearContext = () => {
        setUser(null);
        localStorage.clear();
    };

    const authContextValue = {
        user,
        login,
        logout,
        updateContext,
        clearContext,
        loading,
        jobApply,
        setJobApply,
        jobPreferenceData,
        setJobPreferenceData,
        showJobStatusDialog,
        setShowJobStatusDialog,
    };

    return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
